import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Input, Button, Tooltip } from 'components/ui';
import Modal from 'components/Modal';
import DropdownMenu from 'components/DropdownMenu';
import { updateConversationTitle, deleteConversation } from 'lib/supabase-db';
import { useSidebar } from 'contexts/SidebarContext';

interface HistoryItemProps {
  id: string;
  title: string;
  isActive?: boolean;
  onUpdate?: (id: string, newTitle: string) => void;
  onDelete?: (id: string) => void;
}

const HistoryItem: React.FC<HistoryItemProps> = ({
  id,
  title,
  isActive = false,
  onUpdate,
  onDelete,
}) => {
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(title);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isMobile, setMobileOpen } = useSidebar();

  const handleRename = async () => {
    if (!newTitle.trim()) {
      setError('Title cannot be empty');
      return;
    }

    if (newTitle === title) {
      setRenameModalOpen(false);
      return;
    }

    setIsRenaming(true);
    setError('');

    try {
      await updateConversationTitle(id, newTitle.trim());
      onUpdate?.(id, newTitle.trim());
      setRenameModalOpen(false);
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      setError('Failed to rename conversation. Please try again.');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      await deleteConversation(id);
      onDelete?.(id);
      setDeleteModalOpen(false);

      // Redirect to dashboard if this was the active conversation
      if (isActive) {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      setError('Failed to delete conversation. Please try again.');
      setIsDeleting(false);
    }
  };

  const menuActions = [
    {
      label: 'Rename',
      onClick: () => {
        setNewTitle(title);
        setError('');
        setRenameModalOpen(true);
      },
    },
    {
      label: 'Delete',
      onClick: () => {
        setError('');
        setDeleteModalOpen(true);
      },
      color: 'text-red-600',
      hoverColor: 'hover:bg-red-50',
    },
  ];

  return (
    <>
      <div className="relative group flex items-center z-10">
        <NavLink
          to={`/dashboard/assistant?conversation=${id}`}
          onClick={() => isMobile && setMobileOpen(false)}
          className={`flex-1 min-w-0 py-2 px-2 rounded transition-colors block cursor-pointer hover:bg-gray-400/20 pr-8 ${
            isActive ? 'bg-gray-200' : ''
          }`}
        >
          <Tooltip content={title}>
            <span className="text-black truncate font-medium block text-sm">{title}</span>
          </Tooltip>
        </NavLink>

        {/* Dropdown Menu - visible on hover */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
          <DropdownMenu actions={menuActions} />
        </div>
      </div>

      {/* Rename Modal */}
      <Modal
        isOpen={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        size="sm"
        title="Rename Conversation"
      >
        <div className="space-y-4">
          <div>
            <Input
              label="Conversation Title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Enter conversation title"
              error={error}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                }
              }}
              autoFocus
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRenameModalOpen(false)}
              disabled={isRenaming}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleRename}
              disabled={isRenaming || !newTitle.trim()}
              loading={isRenaming}
            >
              {isRenaming ? 'Renaming...' : 'Rename'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        size="sm"
        title="Delete Conversation"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Are you sure you want to delete this conversation? This action cannot be undone.
          </div>

          <div className="p-3 bg-gray-50 rounded border">
            <p className="font-medium text-gray-900 truncate">{title}</p>
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{error}</div>}

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
              loading={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default HistoryItem;
